import { getCsrfToken } from './auth';

/**
 * The one place that talks to the Integrations API (`/api/integrations/*`).
 *
 * Same contract as `riskApi.js`: CSRF on every write, FastAPI's `detail` surfaced as the error
 * message. Writes are POST/DELETE only — the server checks CSRF on those two methods, never PUT.
 */
async function call(method, url, body) {
  const opts = { method, headers: {}, credentials: 'include', cache: 'no-store' };
  if (body !== undefined) {
    opts.headers['Content-Type'] = 'application/json';
    opts.body = JSON.stringify(body);
  }
  if (method !== 'GET') opts.headers['X-CSRF-Token'] = getCsrfToken();
  const res = await fetch(url, opts);
  let data = null;
  try { data = await res.json(); } catch (_) { /* a 500 can arrive as HTML */ }
  if (!res.ok) {
    const detail = data?.detail;
    const err = new Error((typeof detail === 'string' ? detail : detail?.message) || `Request failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return data;
}

const B = '/api/integrations';
const enc = encodeURIComponent;

export const intgApi = {
  list: () => call('GET', B),
  create: (body) => call('POST', B, body),
  get: (id) => call('GET', `${B}/${enc(id)}`),
  save: (id, body) => call('POST', `${B}/${enc(id)}`, body),
  remove: (id) => call('DELETE', `${B}/${enc(id)}`),
  duplicate: (id) => call('POST', `${B}/${enc(id)}/duplicate`),
  testOrcanos: (id, body) => call('POST', `${B}/${enc(id)}/test-orcanos`, body),
  testJira: (id, body) => call('POST', `${B}/${enc(id)}/test-jira`, body),
  oauthStart: (id, body) => call('POST', `${B}/${enc(id)}/jira/oauth/start`, body),
  // provider: 'jira' (the Atlassian app) or 'github' (the GitHub OAuth App) — each account's own.
  oauthApp: (provider = 'jira') => call('GET', `${B}/oauth-app?provider=${enc(provider)}`),
  saveOauthApp: (body, provider = 'jira') => call('POST', `${B}/oauth-app?provider=${enc(provider)}`, body),
  oauthDisconnect: (id) => call('POST', `${B}/${enc(id)}/jira/disconnect`),
  githubOauthStart: (id, body) => call('POST', `${B}/${enc(id)}/github/oauth/start`, body),
  githubDisconnect: (id) => call('POST', `${B}/${enc(id)}/github/disconnect`),
  jiraProjects: (id) => call('GET', `${B}/${enc(id)}/jira/projects`),
  jiraIssueTypes: (id, project) => call('GET', `${B}/${enc(id)}/jira/issuetypes?project=${enc(project)}`),
  jiraFields: (id, project, issuetype) =>
    call('GET', `${B}/${enc(id)}/jira/fields?project=${enc(project)}&issuetype=${enc(issuetype)}`),
  orcanosFields: (id) => call('GET', `${B}/${enc(id)}/orcanos/fields`),
  enable: (id, enabled) => call('POST', `${B}/${enc(id)}/enable`, { enabled }),
  registerWebhook: (id) => call('POST', `${B}/${enc(id)}/webhook`),
  runNow: (id) => call('POST', `${B}/${enc(id)}/run-now`),
  dryRun: (id, body) => call('POST', `${B}/${enc(id)}/dry-run`, body),
  tryOne: (id, body) => call('POST', `${B}/${enc(id)}/try-one`, body),
  runs: (id) => call('GET', `${B}/${enc(id)}/runs`),
  events: (id, { action, runId, beforeId } = {}) => {
    const q = new URLSearchParams();
    if (action) q.set('action', action);
    if (runId) q.set('run_id', runId);
    if (beforeId) q.set('before_id', beforeId);
    const s = q.toString();
    return call('GET', `${B}/${enc(id)}/events${s ? `?${s}` : ''}`);
  },
  retry: (eventId) => call('POST', `${B}/events/${enc(eventId)}/retry`),
};

/** The Traceability config API's project + filter pickers, reused as is. */
export async function orcanosProjects() {
  const r = await fetch('/api/config/projects', { credentials: 'include', cache: 'no-store' });
  const d = await r.json();
  return Array.isArray(d.projects) ? d.projects : [];
}

export async function orcanosFilters(projectId, code, label) {
  const q = new URLSearchParams({ project_id: projectId, item_type: code, item_type_label: label || '' });
  const r = await fetch(`/api/config/filters?${q}`, { credentials: 'include', cache: 'no-store' });
  if (!r.ok) return { available: false, filters: [] };
  return r.json();
}

export const POLL_CHOICES = [
  { value: 1, label: '1 min' },
  { value: 5, label: '5 min' },
  { value: 10, label: '10 min' },
  { value: 60, label: '60 min' },
  { value: 1440, label: '24 hours' },
];

export function pollLabel(minutes) {
  if (minutes === 1440) return 'Every 24 hours';
  return `Every ${minutes} min`;
}

export const DIRECTION_LABEL = { to_jira: '→', to_orcanos: '←', both: '↔' };

/** "Jira" or "GitHub" — the other side of an integration (v4.28.0). */
export const partnerOf = (integ) => (integ?.connector === 'github' ? 'GitHub' : 'Jira');

/** A link to the partner issue, from its stored key: PUMP-12 → …/browse/PUMP-12, owner/repo#12 → …/issues/12. */
export function issueUrl(integ, key) {
  if (!key) return '';
  if (integ?.connector === 'github') {
    const m = String(key).match(/^([^#\s]+\/[^#\s]+)#(\d+)$/);
    return m ? `https://github.com/${m[1]}/issues/${m[2]}` : '';
  }
  return integ?.jira_site ? `${integ.jira_site}/browse/${key}` : '';
}
